// External Packages
import PropTypes from 'prop-types';
import { Component } from 'react';
import logger from 'utils/rollbar';

const prismJsLanguages = {
  bash: () => import('prismjs/components/prism-bash'),
  c: () => import('prismjs/components/prism-c'),
  clike: () => import('prismjs/components/prism-clike'),
  css: () => import('prismjs/components/prism-css'),
  elixir: () => import('prismjs/components/prism-elixir'),
  go: () => import('prismjs/components/prism-go'),
  groovy: () => import('prismjs/components/prism-groovy'),
  hcl: () => import('prismjs/components/prism-hcl'),
  html: () => import('prismjs/components/prism-markup'),
  java: () => import('prismjs/components/prism-java'),
  javascript: () => import('prismjs/components/prism-javascript'),
  js: () => import('prismjs/components/prism-javascript'),
  json: () => import('prismjs/components/prism-json'),
  jsx: () => import('prismjs/components/prism-jsx'),
  kotlin: () => import('prismjs/components/prism-kotlin'),
  markup: () => import('prismjs/components/prism-markup'),
  objectivec: () => import('prismjs/components/prism-objectivec'),
  python: () => import('prismjs/components/prism-python'),
  r: () => import('prismjs/components/prism-r'),
  ruby: () => import('prismjs/components/prism-ruby'),
  rust: () => import('prismjs/components/prism-rust'),
  shell: () => import('prismjs/components/prism-bash'),
  'shell-session': () => import('prismjs/components/prism-shell-session'),
  sql: () => import('prismjs/components/prism-sql'),
  swift: () => import('prismjs/components/prism-swift'),
  typescript: () => import('prismjs/components/prism-typescript'),
  xml: () => import('prismjs/components/prism-markup'),
  yaml: () => import('prismjs/components/prism-yaml'),
};

class CodeBlockSyntaxHighlighter extends Component {
  constructor(props) {
    super(props);
    this.loadDependencies = this.loadDependencies.bind(this);
    this.loadLanguageToHighlight = this.loadLanguageToHighlight.bind(this);
    this.loadPrism = this.loadPrism.bind(this);
  }

  componentDidMount() {
    // Parsing content of the article coming from WP to collect languages
    // that needs to be supported and loaded for code block syntax highlighting
    const parser = new DOMParser();
    const { articleContent } = this.props;
    const pageContentHtml = parser.parseFromString(articleContent, 'text/html');
    const codeBlockElements = pageContentHtml.getElementsByClassName('wp-block-code');

    const languagesToHighlight = [];

    Array.from(codeBlockElements).forEach((codeBlockElement) => {
      const codeBlockElementClasses = codeBlockElement.className;
      if (codeBlockElementClasses.includes('language')) {
        const language = codeBlockElementClasses.split('language-').pop();

        // Pushing only unique language to the Array
        if (-1 === languagesToHighlight.indexOf(language)) {
          languagesToHighlight.push(language);
        }
      }
    });

    this.loadPrism(languagesToHighlight);
  }

  // eslint-disable-next-line class-methods-use-this
  async loadDependencies(language) {
    switch (language) {
      case 'css':
        await prismJsLanguages.markup();
        break;
      case 'jsx':
        await prismJsLanguages.clike();
        await prismJsLanguages.javascript();
        await prismJsLanguages.markup();
        break;
      case 'objectivec':
        await prismJsLanguages.clike();
        await prismJsLanguages.c();
        break;
      case 'shell-session':
        await prismJsLanguages.bash();
        break;
      case 'typescript':
        await prismJsLanguages.clike();
        await prismJsLanguages.javascript();
        break;
      case 'go':
      case 'groovy':
      case 'java':
      case 'javascript':
      case 'js':
      case 'kotlin':
      case 'ruby':
      case 'swift':
        await prismJsLanguages.clike();
        break;
      default:
    }
  }

  async loadLanguageToHighlight(language) {
    try {
      await this.loadDependencies(language);
      await prismJsLanguages[language]();
    } catch (error) {
      logger.error(`Prism.js: Error loading unsupported language ${language}.`, error);
    }
  }

  async loadPrism(languagesToHighlight) {
    // Import base/lightweight configuration of the Prism.js library
    // that doesn't include any languages or plugins
    const Prism = await import('prismjs/components/prism-core');

    // Can't use dynamic import with template literals in this Webpack version
    // e.g. await import(`prismjs/components/prism-${language}`);
    await Promise.all(languagesToHighlight.map(this.loadLanguageToHighlight));

    Prism.highlightAll();
  }

  render() {
    return null;
  }
}

// Component Metadata
CodeBlockSyntaxHighlighter.propTypes = {
  articleContent: PropTypes.string.isRequired,
};

export default CodeBlockSyntaxHighlighter;
